/**
 * Utilitários para tratamento de imagens
 */

// URL padrão para imagens de fallback
const DEFAULT_IMAGE_URL = "https://matrixterra.com.br/data/company_images/logo.jpg";
// Para usuário, preferimos a mesma imagem padrão do site (logo da Terra)
// quando não houver foto.
const DEFAULT_USER_IMAGE = DEFAULT_IMAGE_URL;

/**
 * Formata uma imagem base64 com o tipo especificado
 * @param {string} img - String base64 da imagem
 * @param {string} type - Tipo da imagem ('jpeg', 'png', 'jpg'). Padrão: 'jpeg'
 * @returns {string} Data URL formatada ou null
 */
export const formatBase64Image = (img, type = "jpeg") => {
  if (!img || typeof img !== "string") return null;
  
  // Se já tem o prefixo data:image, retorna como está
  if (img.startsWith("data:image/")) {
    return img;
  }
  
  // Remove prefixo se já existir (caso tenha sido adicionado parcialmente)
  // Também remove espaços em branco e quebras de linha
  const cleanImg = img.replace(/^data:image\/(jpeg|jpg|png);base64,/, "").trim().replace(/\s/g, "");
  
  // Se após limpar ficou vazio, retorna null
  if (!cleanImg || cleanImg.length === 0) {
    return null;
  }
  
  // Normaliza o tipo
  const imageType = type === "jpg" ? "jpeg" : type;
  
  // Tenta validar se é base64 válido (mais permissivo - permite caracteres base64)
  // Mas não rejeita se tiver outros caracteres, apenas tenta usar
  return `data:image/${imageType};base64,${cleanImg}`;
};

/**
 * Retorna uma imagem com fallback se não existir
 * @param {string} img - String base64 ou URL da imagem
 * @param {string} fallback - URL ou base64 de fallback. Se não fornecido, usa padrão
 * @param {string} type - Tipo da imagem base64 ('jpeg', 'png'). Padrão: 'jpeg'
 * @returns {string} URL da imagem ou data URL
 */
export const getImageWithFallback = (img, fallback = null, type = "jpeg") => {
  if (img && typeof img === "string") {
    // Se já tem o prefixo data:image, retorna como está
    if (img.startsWith("data:image/")) {
      return img;
    }
    
    // Se é uma URL completa (http/https), retorna
    if (img.startsWith("http://") || img.startsWith("https://")) {
      return img;
    }
    
    // Se começa com "/" mas não parece ser base64 (não tem caracteres base64 típicos), trata como URL relativa
    // Base64 JPEG geralmente começa com "/9j/" ou caracteres alfanuméricos
    if (img.startsWith("/")) {
      // Verifica se parece ser base64 (contém caracteres base64 após a barra)
      const afterSlash = img.substring(1);
      const base64Pattern = /^[A-Za-z0-9+/=]+/;
      if (base64Pattern.test(afterSlash) && afterSlash.length > 10) {
        // É base64, formata
        const formatted = formatBase64Image(img, type);
        if (formatted) {
          return formatted;
        }
      } else {
        // É URL relativa, retorna como está
        return img;
      }
    }
    
    // Se é base64 (não começa com http ou /), formata
    const formatted = formatBase64Image(img, type);
    if (formatted) {
      return formatted;
    }
  }
  
  // Retorna fallback ou padrão
  return fallback || DEFAULT_IMAGE_URL;
};

/**
 * Retorna imagem de produto com fallback padrão
 * @param {string} img - String base64 da imagem do produto
 * @returns {string} Data URL ou URL de fallback
 */
export const getProductImage = (img) => {
  return getImageWithFallback(img, DEFAULT_IMAGE_URL, "jpeg");
};

/**
 * Retorna imagem de usuário com fallback padrão
 * @param {string} img - String base64 da imagem do usuário
 * @param {string} userIconPath - Caminho para ícone de usuário padrão
 * @returns {string} Data URL ou URL de fallback
 */
export const getUserImage = (img, userIconPath = null) => {
  // Se for passado um ícone específico (ex.: avatar padrão em telas internas), usamos.
  // Caso contrário, cai no fallback padrão do site (mesmo comportamento de `hasImage`).
  const fallback = userIconPath || DEFAULT_USER_IMAGE;
  return getImageWithFallback(img, fallback, "png");
};

/**
 * Verifica se uma string é uma imagem base64
 * @param {string} str - String a verificar
 * @returns {boolean}
 */
export const isBase64Image = (str) => {
  if (!str || typeof str !== "string") return false;
  
  // Verifica se começa com data:image
  if (str.startsWith("data:image/")) return true;
  
  // Verifica se parece ser base64 (sem prefixo)
  const base64Regex = /^[A-Za-z0-9+/=]+$/;
  return base64Regex.test(str) && str.length > 100;
};

